import React, { useState, useEffect, useCallback } from "react";
import { Search, RefreshCw, Download, Upload, CheckCircle2, RotateCcw, Trash2, X, ArrowUp, ArrowDown, Star, ShieldAlert, Loader2, Image as ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = [
  { id: "needed", label: "Photo needed" },
  { id: "uploaded", label: "Uploaded" },
  { id: "edited", label: "Edited" },
  { id: "published", label: "Published" },
];
const EKINDS = [{ id: "front", label: "Front" }, { id: "rear", label: "Rear / Ports" }, { id: "detail", label: "Detail" }, { id: "label", label: "Label / Serial (private)" }];

export default function AdminPhotos({ adminKey }) {
  const [filter, setFilter] = useState("uploaded");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ products: [], count: 0 });
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manage, setManage] = useState(null); // product object
  const [token, setToken] = useState("");
  const hdr = { "X-Admin-Key": adminKey };
  const fileSrc = (id, v = "web") => `${API}/photos/file/${id}?variant=${v}&t=${encodeURIComponent(token)}`;

  useEffect(() => {
    fetch(`${API}/photos/access_token`, { headers: hdr }).then((r) => r.json()).then((d) => setToken(d.token || "")).catch(() => {});
  }, [adminKey]);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/photos/dashboard?filter=${filter}&q=${encodeURIComponent(q)}`, { headers: hdr })
      .then((r) => r.json()).then((d) => setData(d && d.products ? d : { products: [], count: 0 }))
      .catch(() => setData({ products: [], count: 0 })).finally(() => setLoading(false));
    fetch(`${API}/photos/needed_count`, { headers: hdr }).then((r) => r.json()).then(setCounts).catch(() => {});
  }, [filter, q, adminKey]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const clearSamples = async () => {
    if (!window.confirm("Remove ALL demo/sample photos (and restore products that only had sample photos)? Production uses a separate database.")) return;
    const r = await fetch(`${API}/photos/samples/clear`, { method: "POST", headers: hdr });
    const d = await r.json(); alert(`Removed ${d.deleted} sample photos; restored ${d.restored} products.`); load();
  };

  return (
    <div>
      <div className="mb-4 rounded-xl border p-3 text-[13px]" style={{ borderColor: "var(--line)", background: "var(--green-100)", color: "var(--green-950)" }}>
        <strong>Note:</strong> After you approve & publish photos, run <strong>Publish</strong> (redeploy) once to refresh the static product-image SEO pages. Demo images are marked <span className="rounded px-1.5 py-0.5 text-[11px] font-bold" style={{ background: "var(--yellow-500)", color: "var(--green-950)" }}>SAMPLE</span> and never reach production.
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 rounded-full border p-1" style={{ borderColor: "var(--line)" }}>
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`photos-filter-${f.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 font-heading text-[13px] font-bold transition-colors"
                    style={filter === f.id ? { background: "var(--green-900)", color: "#fff" } : { color: "var(--green-900)" }}>
              {f.label}
              {counts && f.id === "needed" && <span className="rounded-full px-1.5 text-[11px]" style={{ background: filter === f.id ? "rgba(255,255,255,0.2)" : "var(--green-100)" }}>{counts.needed}</span>}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-full border bg-white px-4 py-2" style={{ borderColor: "var(--line)" }}>
            <Search size={16} style={{ color: "var(--muted)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code or name…" data-testid="photos-search" className="w-36 bg-transparent text-[14px] outline-none sm:w-48" />
          </div>
          <button onClick={load} className="inline-flex items-center gap-2 rounded-full border px-4 py-2 font-heading text-[13px] font-bold" style={{ borderColor: "var(--line)", color: "var(--green-900)" }}><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh</button>
          <button onClick={clearSamples} className="inline-flex items-center gap-2 rounded-full border px-4 py-2 font-heading text-[13px] font-bold" style={{ borderColor: "var(--line)", color: "var(--orange-700)" }}><Trash2 size={15} /> Clear samples</button>
        </div>
      </div>

      <p className="mb-3 text-[13px]" style={{ color: "var(--muted)" }} data-testid="photos-count">{data.count} product{data.count !== 1 ? "s" : ""}</p>

      <div className="grid gap-3" data-testid="photos-list">
        {data.products.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-3 rounded-2xl border bg-white p-3" style={{ borderColor: "var(--line)" }} data-testid={`photos-card-${p.code}`}>
            <div className="flex min-w-0 items-center gap-3">
              {p.img ? (
                <img src={p.img} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" style={{ background: "#f3efe6" }} />
              ) : (
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-lg" style={{ background: "#f3efe6", color: "var(--muted)" }}><ImageIcon size={18} /></span>
              )}
              <div className="min-w-0">
                <span className="font-heading text-[13px] font-extrabold" style={{ color: "var(--orange-700)" }}>{p.code || "—"}</span>
                {p.has_sample && <span className="ml-1.5 rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ background: "var(--yellow-500)", color: "var(--green-950)" }}>SAMPLE</span>}
                <span className="block truncate text-[13.5px] font-semibold" style={{ color: "var(--green-950)" }}>{p.name}</span>
                <span className="text-[12px]" style={{ color: "var(--muted)" }}>{p.category} · {(p.photos || []).length} photo{(p.photos || []).length !== 1 ? "s" : ""}</span>
              </div>
            </div>
            {filter === "needed" ? (
              <span className="shrink-0 text-[12px] font-semibold" style={{ color: "var(--muted)" }}>No photos yet</span>
            ) : (
              <button onClick={() => setManage(p)} data-testid={`photos-manage-${p.code}`} className="shrink-0 rounded-full px-4 py-2 font-heading text-[13px] font-bold text-white" style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>Manage photos</button>
            )}
          </div>
        ))}
        {!loading && data.products.length === 0 && <p className="py-10 text-center text-[14px]" style={{ color: "var(--muted)" }}>Nothing here yet.</p>}
      </div>

      {manage && <ManageModal product={manage} adminKey={adminKey} token={token} fileSrc={fileSrc} onClose={() => setManage(null)} onChanged={load} />}
    </div>
  );
}

function ManageModal({ product, adminKey, token, fileSrc, onClose, onChanged }) {
  const [photos, setPhotos] = useState(product.photos || []);
  const [selected, setSelected] = useState([]); // ordered ids
  const [cover, setCover] = useState("");
  const [busy, setBusy] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [upKind, setUpKind] = useState("front");
  const hdr = { "X-Admin-Key": adminKey };

  const reload = useCallback(async () => {
    const r = await fetch(`${API}/photos/dashboard?filter=all&q=${encodeURIComponent(product.code)}`, { headers: hdr });
    const d = await r.json();
    const fresh = (d.products || []).find((x) => x.id === product.id);
    if (fresh) setPhotos(fresh.photos || []);
    onChanged();
  }, [product.id, product.code, adminKey]);

  const publishable = photos.filter((p) => p.kind !== "label");
  const labels = photos.filter((p) => p.kind === "label");
  const originals = publishable.filter((p) => p.stage === "original");
  const edits = publishable.filter((p) => p.stage === "edited");

  const toggle = (id) => setSelected((s) => {
    const next = s.includes(id) ? s.filter((x) => x !== id) : [...s, id];
    if (!next.includes(cover)) setCover(next[0] || "");
    else if (!cover && next.length) setCover(next[0]);
    return next;
  });
  const move = (id, dir) => setSelected((s) => {
    const i = s.indexOf(id); const j = i + dir;
    if (i < 0 || j < 0 || j >= s.length) return s;
    const n = [...s]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const publish = async () => {
    if (!selected.length) { alert("Select at least one photo to publish."); return; }
    setBusy("publish");
    try {
      const r = await fetch(`${API}/photos/publish`, { method: "POST", headers: { ...hdr, "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: product.id, photo_ids: selected, cover_photo_id: cover || selected[0] }) });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail || "Publish failed");
      alert(`Published ${d.published} photo(s). Run Publish (redeploy) to refresh SEO pages.`);
      setSelected([]); setCover(""); reload();
    } catch (e) { alert(e.message); } finally { setBusy(""); }
  };
  const restore = async () => {
    if (!window.confirm("Restore the previous photos/illustration for this product?")) return;
    setBusy("restore");
    try { const r = await fetch(`${API}/photos/restore`, { method: "POST", headers: { ...hdr, "Content-Type": "application/json" }, body: JSON.stringify({ product_id: product.id }) });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail || "Restore failed"); reload(); }
    catch (e) { alert(e.message); } finally { setBusy(""); }
  };
  const del = async (id) => { if (!window.confirm("Delete this draft photo?")) return; await fetch(`${API}/photos/${id}/delete`, { method: "POST", headers: hdr }); reload(); };
  const uploadEdited = async (e) => {
    const f = e.target.files && e.target.files[0]; if (!f) return; setBusy("upload");
    try { const fd = new FormData(); fd.append("product_id", product.id); fd.append("kind", upKind); fd.append("file", f, f.name);
      const r = await fetch(`${API}/photos/upload_edited`, { method: "POST", headers: hdr, body: fd });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail || "Upload failed");
      setOkMsg("Edited photo uploaded ✓"); setTimeout(() => setOkMsg(""), 2500); reload(); }
    catch (err) { alert(err.message); } finally { setBusy(""); e.target.value = ""; }
  };

  const Thumb = ({ p, selectable }) => (
    <div className="relative overflow-hidden rounded-lg border" style={{ borderColor: selected.includes(p.id) ? "var(--green-800)" : "var(--line)", borderWidth: selected.includes(p.id) ? 2 : 1 }} data-testid={`photo-thumb-${p.id}`}>
      <img src={fileSrc(p.id)} alt={p.kind} className="h-28 w-full object-cover" />
      <div className="absolute left-1 top-1 flex gap-1">
        <span className="rounded px-1.5 py-0.5 text-[9px] font-bold text-white" style={{ background: "var(--green-900)" }}>{p.kind}</span>
        {p.status === "published" && <span className="rounded px-1.5 py-0.5 text-[9px] font-extrabold text-white" style={{ background: "var(--orange-700)" }}>LIVE</span>}
        {p.is_sample && <span className="rounded px-1 py-0.5 text-[9px] font-bold" style={{ background: "var(--yellow-500)", color: "var(--green-950)" }}>SAMPLE</span>}
      </div>
      <div className="flex items-center justify-between gap-1 p-1">
        {selectable ? (
          <label className="flex items-center gap-1 text-[11px] font-bold" style={{ color: "var(--green-900)" }}>
            <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} data-testid={`photo-select-${p.id}`} /> Publish
          </label>
        ) : <span className="text-[10px]" style={{ color: "var(--muted)" }}>{p.stage}</span>}
        <span className="flex gap-1">
          <a href={`${API}/photos/download/${p.id}?t=${encodeURIComponent(token)}`} className="grid h-6 w-6 place-items-center rounded border" style={{ borderColor: "var(--line)", color: "var(--green-800)" }} title="Download original"><Download size={12} /></a>
          {p.status !== "published" && <button onClick={() => del(p.id)} className="grid h-6 w-6 place-items-center rounded border" style={{ borderColor: "var(--line)", color: "var(--orange-700)" }} title="Delete"><Trash2 size={12} /></button>}
        </span>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(31,74,56,0.45)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-2xl bg-white p-5" style={{ boxShadow: "var(--shadow)", maxHeight: "92vh", overflowY: "auto" }}>
        <div className="mb-3 flex items-start justify-between gap-3">
          <div><span className="font-heading text-[13px] font-extrabold" style={{ color: "var(--orange-700)" }}>{product.code}</span>
            <h3 className="font-heading text-[18px] font-extrabold" style={{ color: "var(--green-950)" }}>{product.name}</h3></div>
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full" style={{ background: "var(--green-100)" }}><X size={18} /></button>
        </div>

        <div className="mb-3 flex flex-wrap gap-2">
          <a href={`${API}/photos/download_batch?product_id=${product.id}&t=${encodeURIComponent(token)}`} className="inline-flex items-center gap-2 rounded-full border px-3.5 py-2 font-heading text-[12.5px] font-bold" style={{ borderColor: "var(--line)", color: "var(--green-900)" }}><Download size={14} /> Download all originals (zip)</a>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border-2 px-3.5 py-2 font-heading text-[12.5px] font-bold" style={{ borderColor: "var(--green-800)", color: "var(--green-900)" }}>
            {busy === "upload" ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload edited photos
            <select value={upKind} onChange={(e) => setUpKind(e.target.value)} onClick={(e) => e.stopPropagation()} className="ml-1 rounded border bg-white px-1 py-0.5 text-[11px]" style={{ borderColor: "var(--line)" }}>{EKINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}</select>
            <input type="file" accept="image/*" onChange={uploadEdited} className="hidden" data-testid="photos-upload-edited" />
          </label>
          <button onClick={restore} disabled={!!busy} data-testid="photos-restore" className="inline-flex items-center gap-2 rounded-full border px-3.5 py-2 font-heading text-[12.5px] font-bold disabled:opacity-50" style={{ borderColor: "var(--line)", color: "var(--orange-700)" }}><RotateCcw size={14} /> Restore previous</button>
        </div>
        {okMsg && <p className="mb-3 text-[12.5px] font-bold" data-testid="photos-okmsg" style={{ color: "var(--green-800)" }}>{okMsg}</p>}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="mb-2 font-heading text-[13px] font-bold" style={{ color: "var(--green-950)" }}>Originals (from godown)</p>
            {originals.length ? <div className="grid grid-cols-2 gap-2">{originals.map((p) => <Thumb key={p.id} p={p} selectable />)}</div> : <p className="text-[12.5px]" style={{ color: "var(--muted)" }}>None.</p>}
          </div>
          <div>
            <p className="mb-2 font-heading text-[13px] font-bold" style={{ color: "var(--green-950)" }}>Edited (from ChatGPT)</p>
            {edits.length ? <div className="grid grid-cols-2 gap-2">{edits.map((p) => <Thumb key={p.id} p={p} selectable />)}</div> : <p className="text-[12.5px]" style={{ color: "var(--muted)" }}>None yet — download originals, edit in ChatGPT, upload here.</p>}
          </div>
        </div>

        {labels.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 inline-flex items-center gap-1 font-heading text-[13px] font-bold" style={{ color: "var(--green-950)" }}><ShieldAlert size={14} style={{ color: "var(--orange-700)" }} /> Label / serial — private (never published)</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{labels.map((p) => <Thumb key={p.id} p={p} selectable={false} />)}</div>
          </div>
        )}

        {selected.length > 0 && (
          <div className="mt-4 rounded-xl border p-3" style={{ borderColor: "var(--line)", background: "var(--green-100)" }}>
            <p className="mb-2 font-heading text-[13px] font-bold" style={{ color: "var(--green-950)" }}>Publish order & cover ({selected.length})</p>
            <div className="grid gap-2">
              {selected.map((id, i) => { const p = photos.find((x) => x.id === id); if (!p) return null; return (
                <div key={id} className="flex items-center gap-2 rounded-lg bg-white p-2" style={{ border: "1px solid var(--line)" }}>
                  <img src={fileSrc(id)} alt="" className="h-10 w-10 rounded object-cover" />
                  <span className="flex-1 text-[12.5px] font-semibold" style={{ color: "var(--green-950)" }}>{i + 1}. {p.kind}{p.stage === "edited" ? " (edited)" : ""}</span>
                  <label className="flex items-center gap-1 text-[12px] font-bold" style={{ color: "var(--green-900)" }}><input type="radio" name="cover" checked={cover === id} onChange={() => setCover(id)} data-testid={`photo-cover-${id}`} /><Star size={13} /> Cover</label>
                  <button onClick={() => move(id, -1)} className="grid h-7 w-7 place-items-center rounded border" style={{ borderColor: "var(--line)" }}><ArrowUp size={13} /></button>
                  <button onClick={() => move(id, 1)} className="grid h-7 w-7 place-items-center rounded border" style={{ borderColor: "var(--line)" }}><ArrowDown size={13} /></button>
                </div>
              ); })}
            </div>
            <button onClick={publish} disabled={!!busy} data-testid="photos-publish" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full py-3 font-heading text-[14px] font-bold text-white disabled:opacity-60" style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>{busy === "publish" ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Approve & publish selected photos</button>
          </div>
        )}
      </div>
    </div>
  );
}
